import { Component, OnInit, OnDestroy, NgZone, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Sidenavaction } from '../../../../assets/js/application/sidenavaction';
import { BusinessEntityService } from '../../../core/services/business.service';
import BusinessEntity, { Address, BusinessCategory, BusinessType, ProviderID } from '../../../core/models/business.model';
import { CustomerPondService } from '../../../core/services/customer-pond.service';
import CustomerPondModel from '../../../core/models/customer-pond.model';
import FarmSiteModel, { IAddress, ISoilType, IWaterType } from '../../../core/models/farm-site.model';
import { FarmSiteService } from '../../../core/services/farm-site.service';
import FeedLogModel from '../../../core/models/feedlog.model';
import { FeedLogService } from '../../../core/services/feedlog.service';
import { PondCultureService } from '../../../core/services/pond-culture.service';
import PondCultureModel from '../../../core/models/pond-culture.model';
import * as Chart from 'chart.js';
import PersonsModel from '../../../core/models/persons.model';
import { PersonsService } from '../../../core/services/persons.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BusinessTypeService } from '../../../core/services/business-type.service';
import { BusinessCategoryService } from '../../../core/services/business-category.service';
import BusinessCategoryModel from '../../../core/models/business-category.model';
import BusinessTypeModel from '../../../core/models/business-type.model';
import WaterTypeModel from '../../../core/models/water-type.model';
import SoilTypeModel from '../../../core/models/soil-typeModel';
import { WaterTypeService } from '../../../core/services/water-type.service';
import { SoilTypeService } from '../../../core/services/soil-type.service';
import CustomerLeadModel from '../../../core/models/customer-lead.model';
import { CustomerLeadService } from '../../../core/services/customer-lead.service';
import * as moment from 'moment';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { CountriesAPIService } from '../../../core/services/countries.service';
import CountriesModel from '../../../core/models/countries.model';
import { StatesAPIService } from '../../../core/services/states.service';
import { StateModel } from 'src/app/core/models/json.model';
import { USER_SESSION_SERVICE } from 'src/app/core/rbacl/user-session.service';

declare var $: any;

/**
 * @component BusinessEntityFarmProviderComponent
 * @selector app-business-entity-farm-provider
 * @implements {OnInit, OnDestroy}
 *
 * @description
 * The `BusinessEntityFarmProviderComponent` manages the lifecycle and relationships of Farm Provider business entities.
 * It serves as a central hub for creating, updating, and viewing business profiles for providers and their associated farms.
 *
 * ## Key Responsibilities:
 * 1. **Business Entity Management**: Handles CRUD operations for business entities (Farms, Providers).
 * 2. **Farm Site Association**: Manages the creation and linking of farm sites to business entities.
 * 3. **Role-Based Access**: Adapts functionality and data visibility based on user roles (Business Admin, Provider Manager, Employee).
 * 4. **Performance Metrics**: Visualizes farm performance data including Feed vs DOC and ABW vs DOC charts.
 * 5. **Location Management**: Provides hierarchical location selection (Country -> State -> Village) with infinite scroll and geocoding.
 * 6. **Lead Generation**: Converts business registrations into customer leads.
 *
 * ## User Roles & Permissions:
 * - **Business Admin (Provider)**: Can manage their provider business and view associated farmers.
 * - **Provider Manager**: Can view farms assigned to their technicians.
 * - **Employee**: System-wide access to manage all business entities.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: Logged-in business ID.
 * - `personID`: Logged-in user ID.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` loads user context and fetches initial business lists based on role.
 * 2. **Business Selection**: `getBusinessProfileById` fetches detailed profile, farm sites, and metrics for a selected business.
 * 3. **Form Handling**: `addBusinessDetailsSubmit` and `farmProviderUpdate` handle form submissions for creating/updating businesses.
 * 4. **Farm Site Creation**: `addFarmSite` handles the creation of new farm sites linked to the business.
 *
 * @service BusinessEntityService - For business entity CRUD.
 * @service FarmSiteService - For farm site management.
 * @service FeedLogService - For fetching performance metrics.
 * @service PondCultureService - For culture data and charts.
 * @service CountriesAPIService - For location data.
 * @service CommonMethodsService - For error handling.
 */
@Component({
  selector: 'app-your-customers',
  templateUrl: './your-customers.component.html',
  styleUrls: ['./your-customers.component.scss']
})
export class YourCustomersComponent implements OnInit, OnDestroy {
  /** @type {BusinessEntity} The main business entity model being viewed or edited. */
  public businessEntity: BusinessEntity;

  /** @type {BusinessEntity} Model for registering a new business. */
  public registerBusiness: BusinessEntity;

  /** @type {Array<BusinessEntity>} List of available business entities. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {Array<CustomerLeadModel>} List of customer leads. */
  public customerLeads: Array<CustomerLeadModel>;

  /** @type {CustomerLeadModel} Current customer lead model. */
  public customerLead: CustomerLeadModel;

  /** @type {Address} Address model for the business. */
  public address: Address;

  /** @type {Array<CustomerPondModel>} List of customer ponds. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {CustomerPondModel} Single customer pond model. */
  public customerPond: CustomerPondModel;

  /** @type {Array<FarmSiteModel>} List of farm sites associated with the business. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {FarmSiteModel} Single farm site model. */
  public farmSite: FarmSiteModel;

  /** @type {Array<FeedLogModel>} List of feed logs. */
  public feedLogs: Array<FeedLogModel>;

  /** @type {FeedLogModel} Single feed log model for metrics. */
  public feedLog: FeedLogModel;

  /** @type {Array<PersonsModel>} List of persons. */
  public persons: Array<PersonsModel>;

  /** @type {PersonsModel} Single person model. */
  public person: PersonsModel;

  /** @type {PondCultureModel} Pond culture model for charts. */
  public pondCulture: PondCultureModel;

  /** @type {Array<BusinessCategoryModel>} List of business categories. */
  public businessCategories: Array<BusinessCategoryModel>;

  /** @type {Array<BusinessTypeModel>} List of business types. */
  public businessTypes: Array<BusinessTypeModel>;

  /** @type {BusinessType} Selected business type. */
  public businessType: BusinessType;

  /** @type {BusinessCategory} Selected business category. */
  public businessCategory: BusinessCategory;

  /** @type {ProviderID} Provider ID model. */
  public providerId: ProviderID;

  /** @type {IAddress} Address model for farm site. */
  public farmSiteAddress: IAddress;

  /** @type {Array<WaterTypeModel>} List of water types. */
  public waterTypes: Array<WaterTypeModel>;

  /** @type {Array<SoilTypeModel>} List of soil types. */
  public soilTypes: Array<SoilTypeModel>;

  /** @type {IWaterType} Selected water type. */
  public waterType: IWaterType;

  /** @type {ISoilType} Selected soil type. */
  public soilType: ISoilType;

  /** @type {FeedLogModel} Feed log model for pond level data. */
  public pondLevelFeedLog: FeedLogModel;

  /** @type {FormGroup} Reactive form for business registration. */
  public registerForm: FormGroup;

  /** @type {BusinessEntity} Pincode validation result. */
  public pinCodes: BusinessEntity;

  /** @type {FormGroup} Reactive form for adding farm sites. */
  public addFarmsiteForm: FormGroup;

  /** @type {string} Logged-in user ID. */
  public loginPersonID: string;

  /** @type {boolean} Page loading state flag. */
  public pgLoad = false;

  /** @type {boolean} Response loading state flag. */
  public responseLoader = false;

  /** @type {string} Logged-in user role. */
  public loginPersonType: string;

  /** @type {Array<any>} List of farm site IDs. */
  public farmsiteIds = new Array();

  /** @type {string} Selected farm site ID. */
  public farm_site_id: string;

  /** @type {any} Selected pond ID. */
  public pond_id: any;

  /** @type {any[]} Dropdown list for active ponds. */
  public activepondsDetails_dropdown: any[];

  /** @type {any} Chart canvas element. */
  public canvas: any;

  /** @type {any} Chart context. */
  public ctx: any;

  /** @type {any} Document object. */
  public doc: any;

  /** @type {boolean} Flag for chart data availability. */
  public myChartNodata: boolean;

  /** @type {boolean} Flag for sampling chart data availability. */
  public mySamplingvsDocData: boolean;

  /** @type {number} Total area count. */
  public areaCount: number;

  /** @type {string} Logged-in user business name. */
  public loginUserBusinessName: string;

  /** @type {boolean} Flag indicating form submission. */
  public submitted: boolean;

  /** @type {Array<any>} List of countries. */
  public countries = new Array();

  /** @type {Object} Generic item object. */
  public item: Object;

  /** @type {string} Search keyword. */
  public keyword: string;

  /** @type {boolean} Flag to show submit button. */
  public submitButton: boolean;

  /** @type {boolean} Flag to show update button. */
  public updateButton: boolean;

  /** @type {boolean} Flag to show farm site form. */
  public showsiteform: boolean;

  // Validation form declaration
  /** @type {boolean} Flag indicating farm site form submission. */
  public farmSitesubmitted = false;

  /** @type {Date} Start date for contracts. */
  public startDate: Date;

  /** @type {Date} End date for contracts. */
  public endDate: Date;

  /** @type {string} Country code. */
  public countryCode: string;

  /** @type {Array<any>} Array of years for dropdown. */
  public yearStartedArr = new Array();

  /** @type {Array<any>} List of villages. */
  public villageList = new Array();

  /** @type {BusinessEntity} Logged-in business entity. */
  public loginBusinessEntity: BusinessEntity;

  /** @type {BusinessCategoryModel[]} Duplicate list of business categories. */
  public dupbusinessCategories: BusinessCategoryModel[];

  /** @type {BusinessTypeModel[]} Duplicate list of business types. */
  public dupbusinessTypes: BusinessTypeModel[];

  /** @type {BusinessEntity} Selected business entity for viewing. */
  public selectedBusinessEntity: BusinessEntity;

  /** @type {Array<PersonsModel>} List of business admin persons. */
  public businessAdminPersons: Array<PersonsModel>;

  /** @type {any} Application permissions. */
  public appPermission: any;

  /** @type {number} Count of farms for business admin. */
  public FarmsForBusinessAdmin: number;

  /** @type {FarmSiteModel} Lat/Long data. */
  public latLongs: FarmSiteModel;

  /** @type {Array<any>} List of states. */
  public states: Array<any>;

  /** @type {Array<any>} List of villages. */
  public villages: Array<any>;

  /** @type {Array<any>} Filtered states list. */
  public filteredStates = new Array();

  /** @type {Array<any>} Filtered villages list. */
  public filteredVillages = new Array();

  /** @type {any[]} Limited villages list for pagination. */
  public limitedVillagesList: any[] = [];

  /** @type {Object} Selected state item. */
  public stateitem: Object;

  /** @type {Object} Selected village item. */
  public villageItem: Object;

  /** @type {string} State search keyword. */
  public statekeyword: string;

  /** @type {string} Village search keyword. */
  public villagekeyword: string;

  /** @type {string} Selected country name. */
  public country: string;

  /** @type {boolean} Flag indicating village change. */
  public villageChanged: boolean = false;

  /** @type {boolean} Flag to disable state/village fields. */
  public stateVillageDisabled: boolean = false;

  /** @type {boolean} Flag to prevent double clicks on provider business. */
  public providerBusinessDoubleClick: boolean;

  /** @type {boolean} Flag to prevent double clicks on location. */
  public locationDoubleClick: boolean;

  /** @type {string} Logged-in business category. */
  public loginBusinessCategory: string;

  /** @type {boolean} Flag for dropdown state. */
  public isDropdownOpen = false;

  /** @type {any[]} Filtered options for dropdowns. */
  public filteredOptions: any[] = [];

  /** @type {string} Country search text. */
  public countrySearch: string = 'Enter country name';

  /** @type {string} Logged-in business ID. */
  public loginBusinessID: string;

  /** @type {string} Feed provider person ID. */
  public feedProviderPersonID: string;

  /** @type {string} Processed farm name. */
  public finalFarmName: string;

  /** @type {number} Count of form fields. */
  public formFieldCount: number = 0;

  /** @type {boolean} Flag to show/hide form. */
  public showForm: boolean = false;

  /**
   * Constructor for BusinessEntityFarmProviderComponent.
   * Initializes services and reactive forms.
   *
   * @param {StatesAPIService} statesAPIService - Service for state data.
   * @param {CountriesAPIService} countriesAPIService - Service for country data.
   * @param {ToastrService} toasterService - Service for toast notifications.
   * @param {CustomerLeadService} customerLeadService - Service for customer leads.
   * @param {WaterTypeService} waterTypeService - Service for water types.
   * @param {SoilTypeService} soilTypeService - Service for soil types.
   * @param {FarmSiteService} farmSiteService - Service for farm sites.
   * @param {BusinessTypeService} businessTypeService - Service for business types.
   * @param {BusinessCategoryService} businessCategoryService - Service for business categories.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {BusinessEntityService} businessEntityService - Service for business entities.
   * @param {PersonsService} personService - Service for persons.
   * @param {PondCultureService} pondCultureService - Service for pond culture.
   * @param {FeedLogService} feedlogService - Service for feed logs.
   * @param {FarmSiteService} farmsiteService - Service for farm sites (duplicate injection).
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {ActivatedRoute} route - Active route service.
   * @param {CustomerPondService} customerPondService - Service for customer ponds.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {Router} router - Angular Router.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   * @param {NgZone} ngZone - Angular Zone service.
   * @param {ChangeDetectorRef} cdr - Change detector reference.
   */
  constructor(public statesAPIService: StatesAPIService, public countriesAPIService: CountriesAPIService, private toasterService: ToastrService, public customerLeadService: CustomerLeadService, public waterTypeService: WaterTypeService, public soilTypeService: SoilTypeService, public farmSiteService: FarmSiteService, public businessTypeService: BusinessTypeService, public businessCategoryService: BusinessCategoryService, public formBuilder: FormBuilder, public businessEntityService: BusinessEntityService, public personService: PersonsService, public pondCultureService: PondCultureService, public feedlogService: FeedLogService, public farmsiteService: FarmSiteService, public sidenavaction: Sidenavaction, public route: ActivatedRoute, public customerPondService: CustomerPondService, public commonMethodsService: CommonMethodsService,
    public router: Router, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService, private ngZone: NgZone, private cdr: ChangeDetectorRef) {
    this.registerBusiness = new BusinessEntity();
    this.customerLead = new CustomerLeadModel();
    this.address = new Address();
    this.businessType = new BusinessType();
    this.businessCategory = new BusinessCategory();
    this.providerId = new ProviderID();
    this.farmSite = new FarmSiteModel();
    this.farmSiteAddress = new IAddress();
    this.waterType = new IWaterType();
    this.soilType = new ISoilType();
    this.villageList = new Array();
    this.showsiteform = false;
    // Business Details registraion validation
    this.registerForm = this.formBuilder.group({
      businessName: ['', [Validators.required,
      Validators.minLength(5),
      Validators.maxLength(30),
      Validators.pattern('^[a-zA-Z ]*$')
      ]],
      businessCode: ['', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(5),
        Validators.pattern('^[a-zA-Z0-9-]+$')
      ]],
      businessType: ['', Validators.required],
      email: ['', [Validators.pattern('[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-z]{2,6}$')
      ]],
      mobilenumber: ['', Validators.required],
      countryName: ['', [Validators.required]],
      category: ['', [Validators.required]],
      dialCode: ['', [Validators.required]]
    });
    // validation for farm site
    this.addFarmsiteForm = this.formBuilder.group({
      farmNameAtLocation: ['', [Validators.required,
      Validators.minLength(3),
      Validators.maxLength(30),
      Validators.pattern('^[a-zA-Z\\s\\W]*$')
      ]],
      yearStarted: ['', Validators.required],
      waterTypeId: ['', Validators.required],
      soilTypeId: ['', Validators.required],
      villageName: ['', [Validators.required]],
      stateName: ['', [Validators.required]],
      providerTech: [null],
    });
    // End validation for farm site

    // Calculate field count after form is built
    this.formFieldCount = Object.keys(this.registerForm.controls).length + Object.keys(this.addFarmsiteForm.controls).length;
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Sets previous route.
   * - Initializes form state and flags.
   * - Fetches reference data (Countries, Business Types, etc.).
   * - Loads business entities based on user role.
   */
  public async ngOnInit() {
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.appPermission = this.route.snapshot.data['rolepermission'];
    this.pgLoad = false;
    this.providerBusinessDoubleClick = false;
    this.locationDoubleClick = false;
    this.closeForm();
    this.submitButton = false;
    this.updateButton = false;
    this.showsiteform = false;
    this.businessEntities = [];
    this.countries = await this.countriesAPIService.getAllCountriesList();
    if (this.loginPersonType != 'Business Admin' && this.loginBusinessCategory != 'Provider') {
      this.countrySearch = 'India';
      this.address.country = 'India';
      if (this.countries.length > 0) {
        this.filteredOptions = this.countries;
        this.getDialCode();
      }
    }
    // loginperson type details
    this.loginPersonType = localStorage.getItem('loginPersonType');
    this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
    this.loginBusinessCategory = localStorage.getItem('loginBusinessCategory');
    this.loginPersonID = localStorage.getItem('personID');
    this.loginBusinessID = localStorage.getItem('businessID');
    this.sidenavaction.x_navigation();
    this.responseLoader = true;
    this.areaCount = 0;
    this.yearStartedArr = new Array();
    this.businessType.businessTypeID = '';
    this.businessCategory.businessCategoryID = '';
    this.businessCategory.name = '';
    this.businessType.name = '';
    let curryear = new Date().getFullYear();
    for (let i = 1947; i < curryear; i++) {
      this.yearStartedArr.push(i + 1);
    }
    // end get year started
    try {
      if ((this.loginPersonType == 'Business Admin' && this.loginBusinessCategory == 'Provider') || this.loginPersonType == 'EMPLOYEE') {
        let businessEntities = await this.businessEntityService.getAllfarmerByProviderID(USER_SESSION_SERVICE.currentBusinessId);
        this.businessEntities = businessEntities && businessEntities.length > 0 ? businessEntities.filter((businessObject: any) => businessObject.id != USER_SESSION_SERVICE.currentBusinessId) : []
      } else {
        this.persons = await this.personService.getAllTechniciansByProviderManagerId(this.loginPersonID);
        let technicianIds = this.persons.length > 0 ? _.map(this.persons, 'id') : [];
        if (technicianIds.length > 0) {
          let technicianFarmsFarmsites = await this.farmsiteService.getFarmeSitesByTechnicianIDs(technicianIds);
          if (technicianFarmsFarmsites && technicianFarmsFarmsites.length > 0) {
            this.businessEntities = _.uniqBy(_.map(technicianFarmsFarmsites, 'businessID'), 'id');
          }
        }
      }
      this.pgLoad = true;
      this.businessEntity = await this.businessEntityService.getOneByID(USER_SESSION_SERVICE.currentBusinessId);
      let persons = await this.personService.getPersonBusinessPersonsByBusinessID(this.loginBusinessID);
      if (this.loginBusinessCategory == 'Provider' || this.loginPersonType == 'EMPLOYEE') {
        this.businessTypes = [];
        this.businessCategories = [];
        this.dupbusinessTypes = [];
        this.dupbusinessCategories = [];
        this.businessAdminPersons = persons.persons.filter(tech => tech && tech.personType && tech.personType[0] && tech.personType[0].name == 'Provider Technician')
        this.businessCategories = (await this.businessCategoryService.getAllBusinessCategories()).filter(obj => ['61890be727c16c001eb0d9a6', '61890d9027c16c001eb0d9a7'].includes(obj.id)); // Only farm and provider categories are being filtered
        this.businessTypes = (await this.businessTypeService.getAllBusinessTypes()).filter(obj => ['618916f2b4391c6d48137e3d', '6736dc97bd0e78e8ddaf97d8', '618a17f20f339c001e991b05'].includes(obj.id));
        this.dupbusinessCategories = this.businessCategories;
      }
      this.FarmsForBusinessAdmin = this.businessEntities && this.businessEntities.length > 0 ? this.businessEntities.length : 0;
      this.waterTypes = await this.waterTypeService.getAllWaterTypes();
      this.soilTypes = await this.soilTypeService.getAllSoilTypes();
      this.customerLeads = await this.customerLeadService.getAllCustomerLeads();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getBusinessTypesByCategory
   * @description Filters business types based on the selected business category.
   * Auto-selects if only one type is available.
   */
  public getBusinessTypesByCategory() {
    this.dupbusinessTypes = this.businessTypes.filter((obj) => obj.businessCategoryID === this.businessCategory.businessCategoryID);
    if (this.dupbusinessTypes.length == 1) {
      this.businessType.businessTypeID = this.dupbusinessTypes[0].id;
    } else {
      this.businessType.businessTypeID = '';
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description
   * Lifecycle hook that is called when the component is destroyed.
   * Cancels all ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests for BusinessEntityService, BusinessCategoryService,
   * BusinessTypeService, WaterTypeService, SoilTypeService, and CustomerLeadService.
   */
  public ngOnDestroy(): void {
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.businessCategoryService.cancelOnGoingHttpRequests();
    this.businessTypeService.cancelOnGoingHttpRequests();
    this.waterTypeService.cancelOnGoingHttpRequests();
    this.soilTypeService.cancelOnGoingHttpRequests();
    this.customerLeadService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getDialCode
   * @description Retrieves and sets the dialing code for the selected country.
   * @side-effect Updates `registerBusiness.dialingCode`.
   */
  public getDialCode() {
    let item = this.countries.find(code => code.name == this.address.country);
    this.registerBusiness.dialingCode = item.dialingCode[0];
  }

  /**
   * @method getBusinessProfileById
   * @async
   * @description Fetches detailed profile for a selected business entity.
   * Loads farm sites, ponds, and performance metrics for the business.
   * @param {any} id - The ID of the business entity.
   */
  public async getBusinessProfileById(id: any) {
    $('#farmModal').modal({
      backdrop: 'static',
      keyboard: false,
      show: true
    });
    try {
      this.activepondsDetails_dropdown = [];
      this.selectedBusinessEntity = this.businessEntities.find(businessEntity => businessEntity && businessEntity.id === id); // get one by id business
      if (this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Business Admin') {
        this.farmSites = await this.farmsiteService.getFarmeSitesByProviderIDandFarmId(this.selectedBusinessEntity.id, USER_SESSION_SERVICE.currentBusinessId);
      } else {
        this.farmSites = await this.farmsiteService.getFarmeSitesByProviderIDandFarmId(this.selectedBusinessEntity.id, USER_SESSION_SERVICE.currentBusinessId);
      }
      let siteIds = this.farmSites.map(farmsite => farmsite.id);
      this.farmsiteIds = new Array();
      for (let j = 0; j < siteIds.length; j++) {
        let eachRec = siteIds[j];
        let each = '\'' + eachRec + '\'';
        this.farmsiteIds.push(each);
      }
      // for getting site related data 1st block site name/count, total pond, total area
      this.customerPonds = await this.customerPondService.getPondsByFarmSiteId(siteIds);
      let farmSteArray = [];
      if (this.customerPonds && this.customerPonds.length > 0) {
        for (let pond = 0; pond < this.customerPonds.length; pond++) {
          let eachrec = this.customerPonds[pond];
          let each = eachrec.FarmSiteDetails[0];
          farmSteArray.push(each);
          if (eachrec.PondCultureData && eachrec.PondCultureData.length > 0) {
            for (let j = 0; j < eachrec.PondCultureData.length; j++) {
              let eachpondculture = eachrec.PondCultureData[j];
              if (eachpondculture.end_date == null) {
                eachpondculture.pond_name = eachrec.name;
                eachpondculture.pond_id = eachrec.id;
                eachpondculture.farm_site_id = this.customerPonds[pond].farm_site_id;
                this.activepondsDetails_dropdown.push(eachpondculture);
              }
            }
          }
        }
      }
      const uniqIds = new Object();
      let uniqueFarmSites = farmSteArray.filter(obj => !uniqIds[obj.id] && (uniqIds[obj.id] = true));
      for (let a = 0; a < uniqueFarmSites.length; a++) {
        this.areaCount += Number(uniqueFarmSites[a].totalFarmArea); // pond key
      }
      // end
      this.responseLoader = true;
      await this.getSiteLevelMetric();
      await this.getAllPondMetricData();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getSiteLevelMetric
   * @async
   * @description Fetches site-level performance metrics (Biomass, FCR, Consumption).
   */
  public async getSiteLevelMetric() {
    try {
      if (this.farm_site_id && this.farm_site_id != 'undefined') {
        this.feedLog = await this.feedlogService.getSiteLevel(['\'' + this.farm_site_id + '\''], 'null');
        this.responseLoader = true;
      } else if (this.farm_site_id == 'undefined' || !this.farm_site_id) {
        this.feedLog = await this.feedlogService.getSiteLevel(this.farmsiteIds, 'null');
        this.responseLoader = true;
      }
      if (this.feedLog?.totalBiomass) {
        this.feedLog.totalBiomass = this.feedLog.totalBiomass / 1000;
        this.feedLog.totalBiomass = Number(this.feedLog.totalBiomass.toString().match(/^\d+(?:\.\d{0,4})?/));
      }
      if (this.feedLog?.presentBiomass) {
        this.feedLog.presentBiomass = this.feedLog.presentBiomass / 1000;
        this.feedLog.presentBiomass = Number(this.feedLog.presentBiomass.toString().match(/^\d+(?:\.\d{0,4})?/));
      }
      if (this.feedLog?.harvestedBiomass) {
        this.feedLog.harvestedBiomass = this.feedLog.harvestedBiomass / 1000;
        this.feedLog.harvestedBiomass = Number(this.feedLog.harvestedBiomass.toString().match(/^\d+(?:\.\d{0,4})?/));
      }
      if (this.feedLog?.totalConsumption) {
        this.feedLog.totalConsumption = this.feedLog.totalConsumption / 1000;
        this.feedLog.totalConsumption = Number(this.feedLog.totalConsumption.toString().match(/^\d+(?:\.\d{0,4})?/));
      }
      if (this.feedLog?.FCR) {
        this.feedLog.FCR = Number(this.feedLog.FCR.toString().match(/^\d+(?:\.\d{0,4})?/));
      }
      this.setSingleValueForFormLocation();
    } catch (error) {
    }
  }

  /**
   * @method setSingleValueForFormLocation
   * @description Auto-selects the farm site if only one is available.
   */
  public setSingleValueForFormLocation() {
    if (this.farmSites && this.farmSites.length == 1) {
      this.farm_site_id = this.farmSites[0].id; // model-binding key
    }
  }

  /**
   * @method getAllPondMetricData
   * @async
   * @description Fetches aggregate pond-level metrics for all farm sites.
   */
  public async getAllPondMetricData() {
    try {
      this.pondLevelFeedLog = await this.feedlogService.getSiteLevel(this.farmsiteIds, 'null');
      this.responseLoader = true;
      if (this.pondLevelFeedLog?.totalBiomass) {
        this.pondLevelFeedLog.totalBiomass = this.pondLevelFeedLog.totalBiomass / 1000;
        this.pondLevelFeedLog.totalBiomass = Number(this.pondLevelFeedLog.totalBiomass.toString().match(/^\d+(?:\.\d{0,4})?/));
      }
      if (this.pondLevelFeedLog?.presentBiomass) {
        this.pondLevelFeedLog.presentBiomass = this.pondLevelFeedLog.presentBiomass / 1000;
        this.pondLevelFeedLog.presentBiomass = Number(this.pondLevelFeedLog.presentBiomass.toString().match(/^\d+(?:\.\d{0,4})?/));
      }
      if (this.pondLevelFeedLog?.harvestedBiomass) {
        this.pondLevelFeedLog.harvestedBiomass = this.pondLevelFeedLog.harvestedBiomass / 1000;
        this.pondLevelFeedLog.harvestedBiomass = Number(this.pondLevelFeedLog.harvestedBiomass.toString().match(/^\d+(?:\.\d{0,4})?/));
      }
      if (this.pondLevelFeedLog?.totalConsumption) {
        this.pondLevelFeedLog.totalConsumption = this.pondLevelFeedLog.totalConsumption / 1000;
        this.pondLevelFeedLog.totalConsumption = Number(this.pondLevelFeedLog.totalConsumption.toString().match(/^\d+(?:\.\d{0,4})?/));
      }
      if (this.pondLevelFeedLog?.FCR) {
        this.pondLevelFeedLog.FCR = Number(this.pondLevelFeedLog.FCR.toString().match(/^\d+(?:\.\d{0,4})?/));
      }
    } catch (error) {
    }
  }

  /**
   * @method getPondLevelDataByPondId
   * @async
   * @description Fetches metrics and chart data for a specific pond.
   */
  public async getPondLevelDataByPondId() {
    try {
      this.myChartNodata = false;
      this.mySamplingvsDocData = false;
      /*selected pond based display pond level data*/
      if (this.pond_id && this.pond_id != 'undefined') {
        const item = this.activepondsDetails_dropdown.find(pondId => pondId && pondId.pond_id == this.pond_id);
        this.pondLevelFeedLog = await this.feedlogService.getPondsByFarmsites(item.pond_id, item.id);
        if (this.pondLevelFeedLog.totalBiomass) {
          this.pondLevelFeedLog.totalBiomass = this.pondLevelFeedLog.totalBiomass / 1000;
          this.pondLevelFeedLog.totalBiomass = Number(this.pondLevelFeedLog.totalBiomass.toString().match(/^\d+(?:\.\d{0,4})?/));
        }
        if (this.pondLevelFeedLog.presentBiomass) {
          this.pondLevelFeedLog.presentBiomass = this.pondLevelFeedLog.presentBiomass / 1000;
          this.pondLevelFeedLog.presentBiomass = Number(this.pondLevelFeedLog.presentBiomass.toString().match(/^\d+(?:\.\d{0,4})?/));
        }
        if (this.pondLevelFeedLog.totalHarvestCount) {
          this.pondLevelFeedLog.totalHarvestCount = this.pondLevelFeedLog.totalHarvestCount / 1000;
          this.pondLevelFeedLog.totalHarvestCount = Number(this.pondLevelFeedLog.totalHarvestCount.toString().match(/^\d+(?:\.\d{0,4})?/));
        }
        if (this.pondLevelFeedLog.totalConsumption) {
          this.pondLevelFeedLog.totalConsumption = this.pondLevelFeedLog.totalConsumption / 1000;
          this.pondLevelFeedLog.totalConsumption = Number(this.pondLevelFeedLog.totalConsumption.toString().match(/^\d+(?:\.\d{0,4})?/));
        }
        if (this.pondLevelFeedLog.FCR) {
          this.pondLevelFeedLog.FCR = Number(this.pondLevelFeedLog.FCR.toString().match(/^\d+(?:\.\d{0,4})?/));
        }
        /*display garph*/
        this.pondCulture = await this.pondCultureService.getPondSumaryByFarmSite(item.farm_site_id, item.pond_id, item.id);
        this.responseLoader = true;
        if (this.pondCulture !== undefined) {
          // prepare feedlogvs doc charts
          let docchart = {
            title: 'Feedlog vs DOC',
            lable: [],
            data: [],
            type: 'line',
            fill: false,
            chartid: 'myChart'
          };
          let feedlogDetails = this.pondCulture.feedlogs || [];
          for (let i = 0; i < feedlogDetails.length; i++) {
            feedlogDetails[i].feedGiven = (Number(feedlogDetails[i].feedGiven)) / 1000;
            let lable = feedlogDetails[i].DOC;
            let data = feedlogDetails[i].feedGiven;
            docchart.lable.push(lable);
            docchart.data.push(data);
          }
          // prepare sampling doc charts
          let samplingdocchart = {
            title: 'ABW vs DOC',
            lable: [],
            data: [],
            type: 'line',
            fill: false,
            chartid: 'samplingChart'
          };
          let samplingDetails = this.pondCulture.samplings || [];
          for (let i = 0; i < samplingDetails.length; i++) {
            let lable = samplingDetails[i].Doc;
            let data = samplingDetails[i].ABW ? samplingDetails[i].ABW : 0;
            samplingdocchart.lable.push(lable);
            samplingdocchart.data.push(data);
          }
          if (docchart.data.length > 0) {
            this.myChartNodata = false;
            this.prepareFeedlogvsDoc(docchart);
          } else {
            this.myChartNodata = true;
          }
          if (samplingdocchart.data.length > 0) {
            this.mySamplingvsDocData = false;
            this.prepareAbwvsDoc(samplingdocchart);
          } else {
            this.mySamplingvsDocData = true;
          }
        }
      } else if (this.pond_id == 'undefined' || !this.pond_id || isNaN(this.pond_id)) {
        await this.getAllPondMetricData();
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method prepareFeedlogvsDoc
   * @description Renders the Feed Log vs DOC chart using Chart.js.
   * @param {any} docchart - The chart configuration object.
   */
  public prepareFeedlogvsDoc(docchart) {
    try {
      this.canvas = document.getElementById(docchart.chartid);
      this.ctx = this.canvas.getContext('2d');
      new Chart(this.ctx, {
        type: docchart.type,
        data: {
          labels: docchart.lable,
          datasets: [{
            label: docchart.title,
            data: docchart.data,
            fill: docchart.fill,
            borderColor: [
              'rgb(84, 216, 255)'
            ],
            borderWidth: 1
          }]
        },
        options: {
          scales: {
            yAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'Feed Given (Kgs)',
              }
            }],
            xAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'DOC'
              }
            }]
          },
          events: ['click'],
          tooltips: {
            callbacks: {
              title: function () {
                return null;
              },
              label: (tooltipItem: any) => {
                return 'Doc:' + tooltipItem.xLabel + ', Feed Given:' + tooltipItem.yLabel + ' Kg(s)';
              }
            }
          }
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method prepareAbwvsDoc
   * @description Renders the ABW vs DOC chart using Chart.js.
   * @param {any} samplingchart - The chart configuration object.
   */
  public prepareAbwvsDoc(samplingchart) {
    try {
      this.canvas = document.getElementById(samplingchart.chartid);
      this.ctx = this.canvas.getContext('2d');
      new Chart(this.ctx, {
        type: samplingchart.type,
        data: {
          labels: samplingchart.lable,
          datasets: [{
            label: samplingchart.title,
            data: samplingchart.data,
            fill: samplingchart.fill,
            borderColor: [
              'rgb(84, 216, 255)'
            ],
            borderWidth: 1
          }]
        },
        options: {
          scales: {
            yAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'ABW (Grams)',
              }
            }],
            xAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'DOC'
              }
            }]
          },
          events: ['click'],
          tooltips: {
            callbacks: {
              title: function () {
                return null;
              },
              label: (tooltipItem: any) => {
                return 'Doc:' + tooltipItem.xLabel + ', ABW:' + tooltipItem.yLabel + ' Gram(s)';
              }
            }
          }
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method modalClose
   * @description Resets modal data when closed.
   */
  public modalClose() {
    this.farm_site_id = undefined;
    this.pond_id = undefined;
    this.feedLog = undefined;
    this.pondLevelFeedLog = undefined;
    this.pondCulture = undefined;
    this.customerPonds = undefined;
  }

  /**
   * @method f
   * @description Getter for easy access to `registerForm` controls.
   * @returns {AbstractControl} Form controls.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method getBusinessCode
   * @async
   * @description Fetches business code suggestions based on the business name.
   */
  public async getBusinessCode() {
    if (this.registerBusiness.businessName != '' && this.registerBusiness.businessName != undefined && this.registerBusiness.businessName.length > 5) {
      try {
        const businessCodes = await this.businessEntityService.getBusinessCodeSuggestions(this.processFarmName(this.registerBusiness.businessName), 'noCode');
        if (businessCodes && businessCodes['suggestions'].length > 0) {
          this.registerBusiness.businessCode = businessCodes['suggestions'][0];
        }
      } catch (error) {
      }
    }
  }

  /**
   * @method onBusinessChange
   * @description Clears business code if business name is empty.
   */
  public onBusinessChange() {
    if (this.registerBusiness.businessName == '' || this.registerBusiness.businessName == undefined) {
      this.registerBusiness.businessCode = '';
    }
  }

  /**
   * @method processFarmName
   * @description Formats the farm name to CamelCase for code generation.
   * @param {string} farmName - The raw farm name.
   */
  public processFarmName(farmName: string) {
    let trimmedFarmName = farmName.trim();
    if (trimmedFarmName.includes(' ')) {
      this.finalFarmName = trimmedFarmName
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');
      return this.finalFarmName;
    } else {
      return trimmedFarmName;
    }
  }

  /**
   * @method addBusinessDetailsSubmit
   * @async
   * @description Submits the new business entity details.
   * Creates the business entity and prepares for farm site creation.
   */
  public async addBusinessDetailsSubmit() {
    try {
      const type = this.businessTypes.find(btype => btype.id == this.businessType.businessTypeID);
      this.businessType.businessTypeID = type.id;
      this.businessType.name = type.name;
      const category = this.businessCategories.find(bCategory => bCategory.id == this.businessCategory.businessCategoryID);
      this.businessCategory.businessCategoryID = category.id;
      this.businessCategory.name = category.name;
      this.registerBusiness.providerID = [{ businessID: "true" }];
      this.registerBusiness.businessType.push(this.businessType);
      this.registerBusiness.businessCategory.push(this.businessCategory);
      this.registerBusiness.address.push(this.address);
      this.registerBusiness.status = 'trial';
      this.registerBusiness = await this.businessEntityService.create(this.registerBusiness);
      this.toasterService.success('Business created successfully', '', {
        timeOut: 2000
      });
      this.countryCode = this.registerBusiness.address[0].countryCode;
      this.submitted = false;
      this.submitButton = false;
      this.updateButton = false;
      this.showsiteform = true;
      this.getMethodsDynamically();
      this.villageChanged = true;
      this.farmSite = new FarmSiteModel();
      this.getStates();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.providerBusinessDoubleClick = false;
    }
  }

  /**
   * @method openForm
   * @description Opens the form for creating a new business entity.
   * Sets default values (e.g., Country = India).
   */
  public openForm() {
    this.submitButton = true;
    this.updateButton = false;
    this.showsiteform = false;
    this.showForm = true;
    const countryObj = this.countries?.find(country => country.name == 'India');
    this.countrySelected(countryObj);
  }

  /**
   * @method f1
   * @description Getter for easy access to `addFarmsiteForm` controls.
   * @returns {AbstractControl} Form controls.
   */
  public get f1() {
    return this.addFarmsiteForm.controls;
  }

  /**
   * @method checkPincode
   * @async
   * @description Fetches latitude and longitude for the given location details.
   * @param {string} village - Village name.
   * @param {string} state - State name.
   * @param {string} mandal - Mandal name.
   * @param {string} district - District name.
   */
  public async checkPincode(village: string, state: string, mandal: string, district: string) {
    try {
      this.latLongs = await this.farmSiteService.latLongsData(village, state, mandal, district);
      if (this.latLongs.results.length > 0) {
        this.farmSiteAddress.latitude = this.latLongs.results[0].geometry.location.lat;
        this.farmSiteAddress.longitude = this.latLongs.results[0].geometry.location.lng;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method addFarmSite
   * @async
   * @description Submits the form to create a new farm site linked to the created business.
   * Validates input, constructs payload, and calls service.
   */
  public async addFarmSite() {
    this.farmSitesubmitted = true;
    this.addFarmsiteForm.markAllAsTouched();
    // stop here if form is invalid
    if (this.addFarmsiteForm.invalid) {
      return;
    }
    this.farmSitesubmitted = false;
    this.locationDoubleClick = true;
    try {
      this.farmSite.businessID = this.registerBusiness.id;
      if (this.feedProviderPersonID) {
        this.farmSite.contract = [
          {
            feedProviderID: USER_SESSION_SERVICE.currentBusinessId,
            feedProviderPersonID: this.feedProviderPersonID,
            startDate: this.startDate,
            endDate: this.endDate
          }
        ];
      } else {
        this.farmSite.contract = [{
          feedProviderID: USER_SESSION_SERVICE.currentBusinessId,
          feedProviderPersonID: this.loginPersonID,
          startDate: this.startDate,
          endDate: this.endDate
        }];
      }
      /*address*/
      if (this.villageChanged == true) {
        this.farmSite.address = {
          village: this.farmSiteAddress.village.toLowerCase().includes(',') ? this.farmSiteAddress.village.split(',')[0] : this.farmSiteAddress.village,
          taluk: this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 2 ? this.farmSiteAddress.village.split(',')[1] : (this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 1 ? this.farmSiteAddress.village.split(',')[0] : this.farmSiteAddress.state),
          state: this.farmSiteAddress.state,
          pincode: this.farmSiteAddress.pincode,
          mandal: this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 2 ? this.farmSiteAddress.village.split(',')[1] : (this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 1 ? this.farmSiteAddress.village.split(',')[0] : this.farmSiteAddress.state),
          district: this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 2 ? this.farmSiteAddress.village.split(',')[2] : (this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 1 ? this.farmSiteAddress.village.split(',')[1] : this.farmSiteAddress.state),
          latitude: this.farmSiteAddress.latitude,
          longitude: this.farmSiteAddress.longitude,
          country: this.address.country
        };
      }
      if (this.waterType.waterTypeId && _.filter(this.waterTypes, ['id', Number(this.waterType.waterTypeId)])) {
        const watertypedata = _.filter(this.waterTypes, ['id', Number(this.waterType.waterTypeId)]);
        this.waterType.name = watertypedata[0].name;
      }
      if (this.soilType.soilTypeId && _.filter(this.soilTypes, ['id', Number(this.soilType.soilTypeId)])) {
        const soiltypedata = _.filter(this.soilTypes, ['id', Number(this.soilType.soilTypeId)]);
        this.soilType.name = soiltypedata[0].name;
      }
      this.farmSite.waterType = this.waterType;
      this.farmSite.soilType = this.soilType;
      this.farmSite.SMEContract = undefined;
      this.farmSite.buyerContract = undefined;
      if (this.farmSite.providerUserConfig.length > 0) {
        this.farmSite.providerUserConfig = [this.farmSite.providerUserConfig];
      }
      this.farmSite = await this.farmSiteService.create(this.farmSite);
      this.toasterService.success('Farmsite created successfully', '', {
        timeOut: 2000
      });
      this.showForm = false;
      this.showsiteform = false;
      this.villageChanged = false;
      this.farmSite = new FarmSiteModel();
      this.registerBusiness = new BusinessEntity();
      this.farmSiteAddress = new IAddress();
      this.address = new Address();
      this.submitted = false;
      this.feedProviderPersonID = undefined;
      this.farmSitesubmitted = false;
      this.filteredVillages = new Array();
      this.filteredStates = new Array();
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.locationDoubleClick = false;
    }
  }

  /**
   * @method addToCustomerLead
   * @async
   * @description Creates a customer lead from the business registration data.
   * Validates mobile number uniqueness before creation.
   * Calls `addBusinessDetailsSubmit` upon success.
   */
  public async addToCustomerLead() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    this.submitted = false;
    this.providerBusinessDoubleClick = true;
    let customerLead = this.customerLeads.filter(lead => lead.mobileNumber == this.registerBusiness.mobileNumber);
    if (customerLead && customerLead.length > 0) {
      this.toasterService.warning('Mobile number already exists');
      this.providerBusinessDoubleClick = false;
    } else {
      let businessType = this.businessTypes.find(businesstype => businesstype.id == this.businessType.businessTypeID);
      try {
        const countryItem = this.countries.find(countryName => countryName && countryName.name == this.address.country);
        this.customerLead.countryCode = countryItem.countryCode;
        this.customerLead.lead_culture = {
          id: 1,
          name: 'Shrimp'
        };
        this.customerLead.mobileNumber = this.registerBusiness.mobileNumber;
        this.customerLead.lead_from = 'Farmer';
        this.customerLead.event = 'Register as farmer';
        this.customerLead.lead_widget = 'bb_web';
        this.customerLead.isActive = true;
        this.customerLead.status = 'Archived';
        this.customerLead.converted = true;
        this.customerLead.lead_reference = 'self';
        this.customerLead.businessType = [{
          id: businessType.id,
          name: businessType.name
        }];
        this.customerLead.status_date = (moment(new Date()).format('YYYY-MM-DD')).toString();
        this.customerLead.generatedDate = (moment(new Date()).format('YYYY-MM-DD')).toString();
        this.customerLead = await this.customerLeadService.create(this.customerLead);
        if (this.customerLead) {
          this.addBusinessDetailsSubmit();
        }
      } catch (error) {
        this.commonMethodsService.userFriendlyAlertMessages(error);
        this.providerBusinessDoubleClick = false;
      }
    }
  }

  /**
   * @method deleteBusinessDetails
   * @async
   * @description Deletes the currently registered business entity if farm site creation is cancelled.
   */
  public async deleteBusinessDetails() {
    try {
      const status = confirm("Are u sure u want to delete a business");
      if (status) {
        await this.businessEntityService.deleteOneByID(this.registerBusiness.id);
        this.showForm = false;
        this.ngOnInit();
      } else {
        this.toasterService.warning('Location creation is mandatory', '', {
          timeOut: 2000
        });
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getBusinessEntityById
   * @async
   * @description Fetches business entity details by ID for editing.
   * @param {any} id - The ID of the business entity.
   */
  public async getBusinessEntityById(id) {
    this.showForm = true;
    this.submitButton = false;
    this.updateButton = true;
    this.providerBusinessDoubleClick = false;
    this.registerBusiness = await this.businessEntityService.getOneByID(id);
    if (this.registerBusiness.address.length > 0) {
      this.address.country = this.registerBusiness.address[0].country;
      this.countrySearch = this.registerBusiness.address[0].country;
      this.registerForm.get('countryName')!.setValue(this.countrySearch);
      this.getDialCode();
    }
    this.businessCategories = this.dupbusinessCategories;
    if (this.registerBusiness.businessCategory.length > 0) {
      this.businessCategory.businessCategoryID = this.registerBusiness.businessCategory[0].businessCategoryID;
      this.getBusinessTypesByCategory();
    }
    if (this.registerBusiness.businessType.length > 0) {
      this.businessType.businessTypeID = this.registerBusiness.businessType[0].businessTypeID;
    }
    if (this.registerBusiness.address.length == 0) {
      this.registerBusiness.address.push({
        houseNo: '',
        street: '',
        landMark: '',
        state: '',
        district: '',
        city: '',
        pincode: '',
        countryCode: '',
        country: this.address.country
      });
    }
    // return this.registerBusiness;
  }

  /**
   * @method farmProviderUpdate
   * @async
   * @description Updates an existing business entity.
   */
  public async farmProviderUpdate() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    // // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    this.submitted = false;
    this.providerBusinessDoubleClick = true;
    const type = this.businessTypes.find(btype => btype.id == this.businessType.businessTypeID);
    const category = this.businessCategories.find(bCategory => bCategory.id == this.businessCategory.businessCategoryID);
    this.registerBusiness.businessType = [
      {
        businessTypeID: type.id,
        name: type.name
      }
    ];
    this.registerBusiness.businessCategory = [
      {
        name: category.name,
        businessCategoryID: category.id
      }
    ];
    this.countrySearch = this.registerBusiness.address[0].country;
    const item = this.countries.find(countryName => countryName.name == this.countrySearch);
    this.registerBusiness.address[0].country = item.name;
    this.registerBusiness.address[0].countryCode = item.countryCode;
    try {
      this.submitted = false;
      const updateRegisterBusiness = Object.assign({}, this.registerBusiness);
      delete updateRegisterBusiness.id;
      this.registerBusiness = await this.businessEntityService.updateOneByID(this.registerBusiness.id, updateRegisterBusiness);
      this.toasterService.success('Business entity updated successfully.', '', {
        timeOut: 2000
      });
      this.showForm = false;
      this.updateButton = false;
      this.registerBusiness = new BusinessEntity();
      this.businessType.businessTypeID = '';
      this.businessCategory.businessCategoryID = '';
      this.businessCategory.name = '';
      this.businessType.name = '';
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.providerBusinessDoubleClick = false;
    }

  }

  /**
   * @method closeForm
   * @description Resets the form and component state to default.
   */
  public closeForm() {
    this.registerForm.reset();
    this.addFarmsiteForm.reset();
    this.showForm = false;
    this.registerBusiness = new BusinessEntity();
    this.farmSite = new FarmSiteModel();
    this.address = new Address();
    this.showsiteform = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
    this.villageChanged = false;
    this.stateVillageDisabled = false;
    this.countrySearch = 'India';
    this.providerBusinessDoubleClick = false;
    this.locationDoubleClick = false;
    this.businessType.businessTypeID = '';
    this.businessCategory.businessCategoryID = '';
    this.businessCategory.name = '';
    this.businessType.name = '';
  }

  public filterCountries(): void {
    const value = (this.registerForm.get('countryName')!.value || '') as CountriesModel | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();

    this.filteredOptions = this.countries.filter(o =>
      o.name.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method countrySelected
   * @description Handles country selection. Updates form and fetches states.
   * @param {any} country - The selected country object.
   */
  public countrySelected(country: any): void {
    this.registerForm.get('countryName')!.setValue(country.name);
    this.isDropdownOpen = false;
    this.countrySearch = country.name;
    this.address.country = country.name;
    this.businessEntity.address[0].country = country.name;
    this.registerBusiness.dialingCode = country.dialCode;
    this.registerForm.get('mobilenumber').clearValidators();
    switch (country.name) {
      case 'Spain':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{9,9}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Indonesia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{5,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Vietnam':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{7,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Nigeria':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{7,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Malaysia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{7,9}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Australia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{5,15}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Ecuador':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{8,8}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Saudi Arabia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{8,9}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      default:
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{10,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
    }
    this.getDialCode();
    this.getStates();
  }

  /**
   * @method filterStates
   * @description Filters the list of states based on user input.
   * @side-effect Updates `filteredStates`.
   */
  /* State Selection Field Start */
  public filterStates(): void {
    const value = (this.addFarmsiteForm.get('stateName')!.value || '') as StateModel | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();

    this.filteredStates = this.states.filter(o =>
      o.name.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method stateSelection
   * @description Handles state selection. Updates form and fetches villages.
   * @param {any} state - The selected state object.
   */
  public stateSelection(state: any): void {
    this.addFarmsiteForm.get('stateName')!.setValue(state.name);
    this.farmSiteAddress.state = state.name;
    this.getVillages(state);
  }
  /* State Selection Field end */



  /**
   * @method getStates
   * @description Retrieves and populates the list of states based on the selected country.
   * Clears previous state/village selections if in edit mode or country changed.
   */
  public getStates(): void {
    this.states = [];
    this.villages = [];
    this.filteredStates = [];
    this.filteredVillages = [];
    if (this.submitButton) {
      this.addFarmsiteForm.get('stateName')!.setValue('');
      this.addFarmsiteForm.get('villageName')!.setValue('');
    }
    const selectedCountryObject = this.countries.find((country: CountriesModel) => country.name == this.countrySearch);
    this.states = selectedCountryObject.states;
    this.filteredStates = this.states;
  }

  /**
    * @method getVillages
    * @async
    * @description Retrieves and populates the list of villages based on the selected state.
    */
  public isLoadingVillages = false;
  public isLoadingMore = false;
  public villagesLoadError = false;
  public visibleVillages: any[] = [];
  public CHUNK_SIZE = 50;
  private currentIndex = 0;
  private searchText = '';
  private isLoadTriggered = false;
  public noDataFound = false;

  public async getVillages(state: any) {
    this.resetVillages();
    this.isLoadingVillages = true;
    this.villagesLoadError = false;
    this.noDataFound = false;

    try {
      const response = await this.statesAPIService.getVillagesByStates(state.name);
      if (response?.[0]?.city_town_village?.length) {
        this.villages = response[0].city_town_village;
        this.loadNextChunk();
      } else {
        this.villagesLoadError = true;
      }
    } catch (err) {
      this.villagesLoadError = true;
    } finally {
      this.isLoadingVillages = false;
    }
  }

  /**
   * @method loadNextChunk
   * @private
   * @description Loads the next chunk of villages for infinite scroll.
   */
  private loadNextChunk() {
    const source = this.searchText
      ? this.villages.filter(v =>
        v['city_town_village_name'].toLowerCase().includes(this.searchText.toLowerCase())
      )
      : this.villages;

    if (source.length === 0) {
      this.noDataFound = true;
      return;
    }

    this.noDataFound = false; // reset when data exists
    const nextSet = source.slice(this.currentIndex, this.currentIndex + this.CHUNK_SIZE);
    this.visibleVillages = [...this.visibleVillages, ...nextSet];
    this.currentIndex += this.CHUNK_SIZE;
  }

  /**
   * @method onVillageScroll
   * @description Handles scroll event on village dropdown to load more items.
   */
  public onVillageScroll() {
    if (this.isLoadTriggered || this.isLoadingMore) return;

    const remaining = this.getFilteredList()?.length - this.visibleVillages.length;
    if (remaining > 0) {
      this.isLoadTriggered = true;
      this.isLoadingMore = true;

      setTimeout(() => {
        this.ngZone.run(() => {
          this.loadNextChunk();
          this.isLoadingMore = false;
          this.isLoadTriggered = false;
          this.cdr.detectChanges();
        });
      }, 300);
    }
  }

  /**
   * @method filterVillages
   * @description Filters the village list based on user input.
   * @param {any} event - The input event.
   */
  public filterVillages(event: any) {
    this.searchText = event.target.value;
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.loadNextChunk();
  }

  /**
   * @method getFilteredList
   * @private
   * @description Helper to get filtered list of villages.
   */
  private getFilteredList() {
    return this.searchText
      ? this.villages.filter(v =>
        v['city_town_village_name'].toLowerCase().includes(this.searchText.toLowerCase())
      )
      : this.villages;
  }

  /**
   * @method resetVillages
   * @private
   * @description Resets village data structures.
   */
  private resetVillages() {
    this.villages = [];
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.searchText = '';
    this.isLoadTriggered = false;
    this.noDataFound = false;
    this.addFarmsiteForm.get('villageName')?.setValue('');
  }

  /**
   * @method onVillageFocus
   * @description Handles focus event on village input.
   */
  public onVillageFocus() {
    if (!this.searchText) {
      // If no search text, just reload first chunk
      this.visibleVillages = [];
      this.currentIndex = 0;
      this.loadNextChunk();
    } else {
      // If search exists, reload search result
      this.visibleVillages = [];
      this.currentIndex = 0;
      this.loadNextChunk();
    }
  }

  /**
   * @method onVillageBlur
   * @description Handles blur event on village input.
   */
  public onVillageBlur() {
    if (this.noDataFound || this.visibleVillages.length == 0) {
      this.clearVillageField();
    }
  }

  /**
   * @method clearVillageField
   * @private
   * @description Clears the village input field.
   */
  private clearVillageField() {
    this.addFarmsiteForm.get('villageName')?.setValue('');
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.searchText = '';
  }

  /**
   * @method villageSelection
   * @description Handles village selection. Updates form and triggers geocoding.
   * @param {any} village - The selected village object.
   */
  public villageSelection(village: any): void {
    this.addFarmsiteForm.get('villageName')!.setValue(village.city_town_village_name);
    this.villageChanged = true;
    this.farmSiteAddress.village = village.city_town_village_name;
    if (this.farmSiteAddress.village) {
      this.checkPincode(this.farmSiteAddress.village, this.farmSiteAddress.state, this.farmSiteAddress.village.toLowerCase().includes(',') ? this.farmSiteAddress.village.split(',')[1] : '', this.farmSiteAddress.village.toLowerCase().includes(',') ? this.farmSiteAddress.village.split(',')[2] : '');
    }
  }

  /**
   * @method navigateToAccountClosurePopupOpenScreen
   * @description Navigates to the account closure screen.
   */
  public navigateToAccountClosurePopupOpenScreen() {
    this.router.navigate(['/home/account-closure']);
  }

  /**
   * @method getMethodsDynamically
   * @description Determines which method to call based on button state (submit or update).
   */
  public getMethodsDynamically() {
    if (this.submitButton) {
      return this.addToCustomerLead();
    } else if (this.updateButton) {
      return this.farmProviderUpdate();
    }
  }
}
